import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import moment from 'moment-timezone';

import { useDefaultToast } from '@/composables/useDefaultToast';
import { ROUTES } from '@/constants/routes';
import { formattedAddress } from '@/helpers/address';
import { formatNir } from '@/helpers/identity';
import { formatPhone } from '@/helpers/phone';
import { capitalizeFirstLetter } from '@/helpers/string';
import { normalizeJobExperience } from '@/helpers/workerJobExperience';
import workerService from '@/modules/worker/services/workerService';
import { useConfigurationStore } from '@/store/configurationStore';

import type {
	FormattedProfile,
	LabeledOperation,
} from '@/components/modal/Profile/previewProfile/types';
import type { ProfilePreviewResponse } from '@/modules/worker/types';
import type { IAddress } from '@/types/address';
import type { LabelValue } from '@/types/common';
import type { IInterimJob, IJobStored } from '@/types/job';
import type { UserRole } from '@/types/user';

import {
	getDepartmentCodeFromZip,
	normalizeProfilePreviewAgencies,
	toAddJobPayload,
	toUpdatePersonalInfoPayload,
} from '@/components/modal/Profile/previewProfile/mappers';
import profilPreviewService from '@/components/modal/Profile/previewProfile/services/profilPreviewService';

import router from '@/router';

export const useProfilPreview = (
	profilIds: string[],
	startingProfileId: string
) => {
	const { t } = useI18n();
	const { toastError, toastSuccess } = useDefaultToast();
	const configStore = useConfigurationStore();

	const totalProfiles = ref<number>(1);
	const currentProfileIndex = ref<number>(1);
	const currentProfile = ref<FormattedProfile | null>(null);
	const updatedProfile = ref<FormattedProfile | null>(null);
	const loading = ref<boolean>(false);
	const formattedUpdatedProfilAddress = ref<string>('');

	const compareAddresses = (
		address1: IAddress | null,
		address2: IAddress | null
	): boolean => {
		if (address1 === null && address2 === null) return true;
		if (address1 === null || address2 === null) return false;

		return (
			(address1.name ?? '') === (address2.name ?? '') &&
			address1.street === address2.street &&
			(address1.complement ?? '') === (address2.complement ?? '') &&
			address1.city === address2.city &&
			(address1.citySlug ?? '') === (address2.citySlug ?? '') &&
			address1.zip === address2.zip &&
			(address1.countryCode ?? '') === (address2.countryCode ?? '') &&
			(address1.inseeCode ?? null) === (address2.inseeCode ?? null) &&
			JSON.stringify(address1.location ?? []) ===
				JSON.stringify(address2.location ?? [])
		);
	};

	const compareJobs = (jobs1: IInterimJob[], jobs2: IInterimJob[]): boolean => {
		if (jobs1.length !== jobs2.length) return false;

		const normalizeJob = (job: IInterimJob) => {
			return `${job.jobId}|${job.experience ?? ''}`;
		};

		const normalizedJobs1 = jobs1.map(normalizeJob).sort();
		const normalizedJobs2 = jobs2.map(normalizeJob).sort();

		return normalizedJobs1.every(
			(job1, index) => job1 === normalizedJobs2[index]
		);
	};

	const normalizePhoneDigits = (phone: string) => phone.replace(/\D/g, '');

	const hasChanges = computed(() => {
		if (!currentProfile.value || !updatedProfile.value) return false;

		return (
			currentProfile.value.firstname !== updatedProfile.value.firstname ||
			currentProfile.value.lastname !== updatedProfile.value.lastname ||
			currentProfile.value.email !== updatedProfile.value.email ||
			normalizePhoneDigits(currentProfile.value.phone) !==
				normalizePhoneDigits(updatedProfile.value.phone) ||
			currentProfile.value.birthDate !== updatedProfile.value.birthDate ||
			!compareAddresses(
				currentProfile.value.address,
				updatedProfile.value.address
			) ||
			!compareJobs(currentProfile.value.jobs, updatedProfile.value.jobs)
		);
	});

	const mapProfilePreviewToFormatted = (
		profile: ProfilePreviewResponse
	): FormattedProfile => {
		return {
			id: profile.id,
			firstname: profile.firstname ?? '',
			lastname: profile.lastname ?? '',
			email: profile.email ?? '',
			phone: formatPhone(profile.phone ?? ''),
			birthDate: profile.birthDate
				? moment(profile.birthDate).format('DD/MM/YYYY')
				: '',
			address: profile.address ?? null,
			nationality: profile.nationalityCode ?? '',
			birthPlace: profile.birthPlace
				? capitalizeFirstLetter(profile.birthPlace)
				: '',
			birthCountry: configStore.resolveCountryCode(profile.birthCountry ?? ''),
			nir: profile.nir ? formatNir(profile.nir) : '',
			jobs: (profile.jobs ?? []).map((job) => ({
				jobId: job.jobId,
				experience: normalizeJobExperience(job.experience),
			})),
			roles: profile.roles as UserRole[],
			cv: profile.cv,
			recruitmentCount: profile.recruitmentCount ?? 0,
			agencies: normalizeProfilePreviewAgencies(profile.agencies),
		};
	};

	const getSelectedProfile = async (profileId: string) => {
		try {
			loading.value = true;
			const profile = await workerService.getProfilePreview(profileId);
			currentProfile.value = mapProfilePreviewToFormatted(profile);
			updatedProfile.value = {
				...currentProfile.value,
				jobs: currentProfile.value.jobs.map((job) => ({ ...job })),
				address: currentProfile.value.address
					? { ...currentProfile.value.address }
					: null,
			};
			formattedUpdatedProfilAddress.value = currentProfile.value.address
				? (formattedAddress(currentProfile.value.address) ?? '')
				: '';
			// biome-ignore lint/correctness/noUnusedVariables: Init Biome
		} catch (error) {
			toastError(t('modals.profilPreview.errors.profileNotFound'));
		} finally {
			loading.value = false;
		}
	};

	const saveProfile = async () => {
		if (!updatedProfile.value || !currentProfile.value) return;

		const current = currentProfile.value;
		const updated = updatedProfile.value;
		const workerId = updated.id;
		const operations: LabeledOperation[] = [];

		const resolveDepartmentRegion = (zip: string) => {
			const departmentCode = getDepartmentCodeFromZip(zip);
			const department = configStore.departmentsOptions.find(
				(d) => d.value === departmentCode
			);
			const regionCode = department?.regionCode ?? '';
			const region = configStore.regionsOptions.find(
				(r) => r.value === regionCode
			);
			return {
				department: departmentCode,
				region: region?.value ?? regionCode,
			};
		};
		const personalInfoPayload = toUpdatePersonalInfoPayload(current, updated, {
			resolveDepartmentRegion,
		});
		if (personalInfoPayload) {
			operations.push({
				label: 'personalInfo',
				promise: profilPreviewService.updatePersonalInfo(
					workerId,
					personalInfoPayload
				),
			});
		}

		const getJobIdSet = (jobs: { jobId: string }[]) =>
			new Set(jobs.map((job) => job.jobId));
		const currentJobIds = getJobIdSet(current.jobs);
		const updatedJobIds = getJobIdSet(updated.jobs);

		const addedJobs = updated.jobs.filter(
			(job) => !currentJobIds.has(job.jobId)
		);
		const removedJobIds = current.jobs
			.filter((job) => !updatedJobIds.has(job.jobId))
			.map((job) => job.jobId);

		const normalizeExperience = (exp: string | null | undefined) =>
			(exp ?? '').trim().toLowerCase();
		const jobsWithExperienceUpdate = updated.jobs.filter((job) => {
			const currentJob = current.jobs.find((j) => j.jobId === job.jobId);
			return (
				currentJob &&
				normalizeExperience(currentJob.experience) !==
					normalizeExperience(job.experience)
			);
		});

		for (const job of addedJobs) {
			const jobOption = configStore.jobOptions.find(
				(opt) => opt.value === job.jobId
			);
			const jobName = jobOption?.label ?? 'Unknown Job';
			const jobPayload = toAddJobPayload(job, jobName);
			operations.push({
				label: `addJob:${job.jobId}`,
				promise: profilPreviewService
					.addJob(workerId, jobPayload)
					.then(() => undefined),
			});
		}

		for (const jobId of removedJobIds) {
			operations.push({
				label: `removeJob:${jobId}`,
				promise: profilPreviewService.removeJob(workerId, jobId),
			});
		}

		for (const job of jobsWithExperienceUpdate) {
			const jobOption = configStore.jobOptions.find(
				(opt) => opt.value === job.jobId
			);
			const jobName = jobOption?.label ?? 'Unknown Job';
			const jobPayload = toAddJobPayload(job, jobName);
			operations.push({
				label: `updateExperience:${job.jobId}`,
				promise: profilPreviewService
					.removeJob(workerId, job.jobId)
					.then(() => profilPreviewService.addJob(workerId, jobPayload))
					.then(() => undefined),
			});
		}

		if (operations.length === 0) {
			return;
		}

		loading.value = true;
		try {
			const results = await Promise.allSettled(
				operations.map((op) => op.promise)
			);

			const failures: string[] = [];
			const successes: string[] = [];

			results.forEach((result, index) => {
				const label = operations[index].label;
				if (result.status === 'rejected') {
					failures.push(label);
				} else {
					successes.push(label);
				}
			});

			if (failures.length === 0) {
				toastSuccess(t('modals.profilPreview.success.saveProfile'));
				currentProfile.value = { ...updated, jobs: [...updated.jobs] };
			} else if (successes.length === 0) {
				toastError(t('modals.profilPreview.errors.saveProfile'));
			} else {
				const failedLabels = failures
					.map((label) => {
						if (label === 'personalInfo')
							return t('modals.profilPreview.errors.personalInfo');
						if (label === 'nir') return t('modals.profilPreview.errors.nir');
						if (label.startsWith('updateExperience:'))
							return t('modals.profilPreview.errors.jobExperience');
						return label;
					})
					.join(', ');
				toastError(
					t('modals.profilPreview.errors.saveProfilePartial', {
						fields: failedLabels,
					})
				);
			}
			// biome-ignore lint/correctness/noUnusedVariables: Init Biome
		} catch (error) {
			toastError(t('modals.profilPreview.errors.saveProfile'));
		} finally {
			loading.value = false;
		}
	};

	const handlePreviousProfile = () => {
		if (currentProfileIndex.value === 1) return;
		currentProfileIndex.value--;
		getSelectedProfile(profilIds[currentProfileIndex.value - 1]);
	};

	const handleNextProfile = () => {
		if (currentProfileIndex.value === totalProfiles.value) return;
		currentProfileIndex.value++;
		getSelectedProfile(profilIds[currentProfileIndex.value - 1]);
	};

	const handleOpenProfil = () => {
		if (!currentProfile.value?.id) return;
		const workerId = currentProfile.value.id;
		const resolvedProfileUrl = router.resolve({
			name: ROUTES.APP.WORKERS.PROFIL.CAREER,
			params: { workerId },
		}).href;
		window.open(resolvedProfileUrl, '_blank', 'noopener,noreferrer');
	};

	const updateProfileField = <K extends keyof FormattedProfile>(
		field: K,
		value: FormattedProfile[K]
	) => {
		if (!updatedProfile.value) return;
		if (updatedProfile.value[field] === value) return;
		updatedProfile.value[field] = value;
	};

	const updateProfileAddress = (value: IAddress | null) => {
		if (!updatedProfile.value) return;
		if (compareAddresses(updatedProfile.value.address, value)) return;
		updatedProfile.value.address = value;
	};

	const handleAddJob = (selectedJob: IJobStored) => {
		if (!updatedProfile.value) return;
		updatedProfile.value.jobs.unshift({
			jobId: selectedJob.value,
			experience: null,
		});
	};

	const handleUpdateJob = (
		jobIndex: number,
		newJobValue: IJobStored | null
	) => {
		if (!updatedProfile.value) return;
		if (!newJobValue?.value) {
			updatedProfile.value.jobs.splice(jobIndex, 1);
			return;
		}
		updatedProfile.value.jobs[jobIndex] = {
			...updatedProfile.value.jobs[jobIndex],
			jobId: newJobValue.value,
		};
	};

	const handleUpdateExperience = (
		jobIndex: number,
		newExperienceValue: LabelValue | null
	) => {
		if (!updatedProfile.value) return;
		const nextExperience = newExperienceValue?.value ?? null;
		if (updatedProfile.value.jobs[jobIndex].experience === nextExperience)
			return;
		updatedProfile.value.jobs[jobIndex] = {
			...updatedProfile.value.jobs[jobIndex],
			experience: nextExperience,
		};
	};

	const removeJob = (jobIndex: number) => {
		if (!updatedProfile.value) return;
		updatedProfile.value.jobs.splice(jobIndex, 1);
	};

	onBeforeMount(() => {
		currentProfileIndex.value =
			// biome-ignore lint/complexity/useIndexOf: Init Biotme
			profilIds.findIndex((profilId) => profilId === startingProfileId) + 1;
		totalProfiles.value = profilIds.length;
		getSelectedProfile(startingProfileId);
	});

	return {
		totalProfiles,
		currentProfileIndex,
		currentProfile,
		updatedProfile,
		loading,
		formattedUpdatedProfilAddress,
		hasChanges,
		handlePreviousProfile,
		handleNextProfile,
		handleOpenProfil,
		updateProfileField,
		updateProfileAddress,
		handleAddJob,
		handleUpdateJob,
		handleUpdateExperience,
		removeJob,
		getSelectedProfile,
		saveProfile,
	};
};
